import { jsVersionWithBuildNumber, openInNewWindow } from './app';
import { injectJS } from './utils';
import { Capacitor } from '@capacitor/core';
import { Build } from '@stencil/core';
import { getStorageKey, setStorageKey } from './storage';
import { AmplitudeEvents, amplitudeLogEvent } from './amplitude';
import { getLoggedInUserId } from './firestore-user';
import { makeRequest } from './make-request';
import { cloudFunctionsDomain, FirebaseAuthUser, fst as firestore } from './firebase';
import {Intercom} from '@capacitor-community/intercom';

// import type firebaseType from 'firebase';
export type IntercomEvent =
  | 'ask_a_trainer'
  | 'attempted_to_review'
  | 'attemped_to_share_completed_challenge'
  | 'attemped_to_share_completed_workout'
  | 'attemped_to_share_on_email'
  | 'attemped_to_share_on_facebook'
  | 'attemped_to_share_on_twitter'
  | 'contacted_support'
  | 'onboarding_completed'
  | 'opened_help_center'
  | 'page_view'
  | 'workout_started'
  | 'meditation_started'
  | 'copied_link'
  | 'exit_beta'
  | 'first_workout_free'
  | 'view_purchase_free_workout'
  | 'view_purchase_no_free_workout'
  | 'clicked_free_workout_button'
  | 'first_free_workout_completed'
  | 'free_workout_limit_reached'
  | 'view_purchase_screen'
  | 'actively-pt'
  | 'previously-pt'
  | 'pt-or-doctor'
  | 'pt-other'
  | 'preloading_error'
  | 'viewed_pregnancy_workouts'
  | 'subscribe_successful'
  | 'signed_out'
  | 'get_new_recommendations';
export interface IntercomRegisterParams {
  userId?: string;
  email?: string;
}
function webIntercom() {
  return window['Intercom'] as Intercom_.IntercomStatic;
}

export async function bootIntercom(
  user: FirebaseAuthUser,
  signUpTimeStamp: number
) {
  const email = user.email;
  const js_version = jsVersionWithBuildNumber;
  if (Build.isBrowser) {
    if (Capacitor.isNativePlatform()) {
      if (!Intercom) return;
      try {
        const params: IntercomRegisterParams = {
          userId: user.uid,
        };
        if (email) {
          params.email = email;
        }
        if (params.userId || params.email) {
          await Intercom.registerIdentifiedUser(params);
        } else {
          await Intercom.registerUnidentifiedUser();
        }
        await Intercom.updateUser({
          customAttributes: {
            js_version,
          },
        });
      } catch (err) {
        console.error('problem booting native intercom', err);
      }
    } else {
      console.debug('not native');
      await injectJS(
        'intercom-script',
        'https://widget.intercom.io/widget/fbnnepod'
      );
      webIntercom()('boot', {
        app_id: 'fbnnepod',
        email,
        created_at: signUpTimeStamp,
        user_id: user.uid,
        js_version,
      });
    }
  }
}

function updateIntercom(metadata?: {[key: string]: any}) {
  // THIS FUNCTION SHOULD NOT BE EXPORTED
  // all intercom updates should be triggered via a cloud function on the user document
  // if it can't be triggered server side - create a separate specific function in this file
  // e.g. toggleMotivationalMessagesIntercom - this preference device specific, and not saved server side

  // You can call Intercom('update') without getting throttled up to 20 times per 30 minutes. After the 20th call, you'll be throttled and the quota of 20 calls will get reset every 30 minutes. Reloading the page will refresh this state. https://developers.intercom.com/installing-intercom/docs/intercom-javascript#section-intercomupdate

  if (Build.isBrowser) {
    if (Capacitor.isNativePlatform()) {
      console.debug('NATIVE');
      Intercom.updateUser({ customAttributes: { ...metadata } });
    } else if (webIntercom()) {
      webIntercom()('update', metadata);
    }
  }
}

export function trackIntercomEvent(eventName: IntercomEvent, metadata?: any) {
  //eventName: The name of the event that occurred. This is presented to your App's admins when filtering and creating segments - a good event name is typically a past tense 'verb-noun' combination, to improve readability, for example updated-plan.
  // const timestamp = Math.floor(new Date().getTime() / 1000);
  // metadata.created_at = timestamp;
  console.debug('tracking event', eventName, metadata);
  if (Build.isBrowser) {
    if (Capacitor.isNativePlatform()) {
      Intercom.logEvent({ name: eventName, data: metadata });
    } else if (webIntercom()) {
      webIntercom()('trackEvent', eventName, metadata);
    }
  }
}

export function trackEventThatTriggersIntercomMessage(
  eventName: IntercomEvent,
  metadata?: any
) {
  // NOTE: this should only be called if we want to trigger a message right after emitting an event
  // You can call Intercom('update') without getting throttled up to 20 times per 30 minutes. After the 20th call, you'll be throttled and the quota of 20 calls will get reset every 30 minutes. Reloading the page will refresh this state.
  // https://developers.intercom.com/installing-intercom/docs/intercom-javascript#section-intercomupdate
  trackIntercomEvent(eventName, metadata);
  setTimeout(() => {
    updateIntercom({ last_request_at: new Date().getTime() / 1000 });
  }, 500);
}

export async function trackIntercomEventOnce(
  intercomEventName: IntercomEvent,
  metadata?: any
) {
  const alreadyTracked = await getStorageKey(intercomEventName);
  if (!alreadyTracked) {
    trackIntercomEvent(intercomEventName, metadata);
    setStorageKey(intercomEventName, 'true');
    console.debug('intercom event logged once', intercomEventName, metadata);
  } else {
    console.debug(
      'intercom event already tracked',
      intercomEventName,
      metadata
    );
  }
}

export function openIntercomMessenger(
  channel: string,
  defaultMessage?: string
) {
  window.location.hash = channel;
  if (Capacitor.isNativePlatform()) {
    if (channel === 'contact-support') {
      Intercom.displayMessenger();
    } else {
      if (defaultMessage) {
        Intercom.displayMessageComposer({ message: defaultMessage });
      } else {
        Intercom.displayMessageComposer({ message: '' });
      }
    }
  } else if (webIntercom()) {
    if (channel === 'contact-support') {
      webIntercom()('showMessages');
    } else {
      webIntercom()('showNewMessage', defaultMessage);
    }
  }
  trackIntercomEvent('contacted_support');
  amplitudeLogEvent(AmplitudeEvents.contacted_support);
}

export function shutdownIntercom() {
  if (Capacitor.isNativePlatform()) {
    Intercom.logout();
  } else if (webIntercom()) {
    webIntercom()('shutdown');
  }
}

export function openIntercomHelpCenter() {
  if (Capacitor.isNativePlatform()) {
    Intercom.displayHelpCenter();
  } else {
    openInNewWindow('https://help.sworkit.com/');
  }
  trackIntercomEvent('opened_help_center');
  amplitudeLogEvent(AmplitudeEvents.opened_help_center);
}

export function dontShowIntercom() {
  if (Capacitor.isNativePlatform()) {
    Intercom.hideInAppMessages();
  }
}

export function canShowIntercom() {
  if (Capacitor.isNativePlatform()) {
    Intercom.displayInAppMessages();
  }
}

export function toggleMotivationalMessagesIntercom(
  motivational_messages: boolean
) {
  return updateIntercom({ motivational_messages });
}

export function toggleProductAnnouncementsIntercom(
  product_announcements: boolean
) {
  return updateIntercom({ product_announcements });
}

export async function tagUserInIntercom(tags: string[]) {
  const uid = getLoggedInUserId();
  if (!uid || !firestore) return;
  try {
    await Promise.all(
      tags.map((tag) =>
        makeRequest({
          url: `${cloudFunctionsDomain}/intercomAddTagToContact?uid=${uid}&tag=${tag}`,
          method: 'POST',
        })
      )
    );
  } catch (err) {
    console.error('error tagging user in intercom', err);
  }
}
